import { parseLine, splitIntoLines } from './parser';
import type { AraClientInit, AraHandlers, AraStreamOptions, StartParams } from './types';

export async function streamAra(opts: AraStreamOptions, h: AraHandlers) {
  const { baseUrl, bearerToken, body, headers = {}, signal } = opts;

  const res = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    h.onError?.(err);
    h.onFinally?.();
    throw err;
  }
  if (!res.body) {
    const err = new Error('ReadableStream missing');
    h.onError?.(err);
    h.onFinally?.();
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { lines, rest } = splitIntoLines(buffer);
      buffer = rest;
      for (const line of lines) {
        const obj = parseLine(line);
        if (!obj) continue;
        if (obj.type === 'begin') h.onBegin?.(obj.metadata as Record<string, unknown>);
        else if (obj.type === 'item') h.onItem?.(obj.content, obj.metadata as Record<string, unknown>);
        else if (obj.type === 'end') h.onEnd?.(obj.metadata as Record<string, unknown>);
      }
    }
    const last = buffer.trim();
    if (last) {
      const obj = parseLine(last);
      if (obj) {
        if (obj.type === 'begin') h.onBegin?.(obj.metadata as Record<string, unknown>);
        else if (obj.type === 'item') h.onItem?.(obj.content, obj.metadata as Record<string, unknown>);
        else if (obj.type === 'end') h.onEnd?.(obj.metadata as Record<string, unknown>);
      }
    }
  } catch (e) {
    if ((e as { name?: string } | null)?.name !== 'AbortError') {
      h.onError?.(e);
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
    h.onFinally?.();
  }
}

export class AraClient {
  private baseUrl: string;
  private bearerToken?: string;
  private defaultHeaders?: Record<string, string>;
  private controller: AbortController | null = null;

  constructor(init: AraClientInit) {
    this.baseUrl = init.baseUrl;
    this.bearerToken = init.bearerToken;
    this.defaultHeaders = init.defaultHeaders;
  }

  cancel() {
    this.controller?.abort();
    this.controller = null;
  }

  async start(params: StartParams, handlers: AraHandlers) {
    this.cancel();
    const controller = new AbortController();
    this.controller = controller;
    const { headers, signal, ...rest } = params;
    const opts: AraStreamOptions = {
      baseUrl: this.baseUrl,
      bearerToken: this.bearerToken,
      body: rest.body,
      headers: { ...this.defaultHeaders, ...headers },
      signal: signal ?? controller.signal,
    };
    return streamAra(opts, handlers);
  }
}


